import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Star, MapPin, ShieldCheck, Briefcase, Award, Heart, Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Card, Spinner, EmptyState } from "../../components/dashboard/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { rupiah, fmtDate, VEHICLE_LABELS } from "../../lib/format";

function BookingForm({ insp, initialType }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: () => api.get("/meta").then((r) => r.data), staleTime: Infinity });
  const [f, setF] = useState({ vehicle_type: insp.vehicle_types.includes(initialType) ? initialType : insp.vehicle_types[0], brand: "", model: "", year: "", plate: "", odometer: "",
    address: "", city: insp.service_areas?.[0] || insp.city, scheduled_date: "", scheduled_time: "09:00", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const price = insp.prices[f.vehicle_type] || 0;
  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const submit = async () => {
    if (!f.address.trim()) return toast.error("Isi alamat lokasi inspeksi.");
    if (!f.scheduled_date) return toast.error("Pilih tanggal inspeksi.");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        inspector_id: insp.user_id, vehicle_type: f.vehicle_type,
        vehicle_info: { brand: f.brand, model: f.model, year: f.year ? Number(f.year) : null, plate: f.plate, odometer: f.odometer ? Number(f.odometer) : null },
        address: f.address, city: f.city, scheduled_date: f.scheduled_date, scheduled_time: f.scheduled_time, notes: f.notes,
      });
      toast.success("Pesanan dibuat! Lanjutkan ke pembayaran.");
      qc.invalidateQueries({ queryKey: ["orders"] }); qc.invalidateQueries({ queryKey: ["user-dashboard"] });
      navigate(`/pengguna/pesanan/${data.order_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Card title="Form Pemesanan" className="sticky top-20" >
      <div className="space-y-3" data-testid="booking-form">
        <div>
          <label className="text-[11px] font-bold text-slate-500">Jenis kendaraan</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {insp.vehicle_types.map((v) => (
              <button key={v} onClick={() => setF((s) => ({ ...s, vehicle_type: v }))} data-testid={`booking-type-${v}`}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${f.vehicle_type === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{VEHICLE_LABELS[v]}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Merek (Toyota)" value={f.brand} onChange={set("brand")} data-testid="booking-brand" className="h-10 text-sm" />
          <Input placeholder="Model (Avanza)" value={f.model} onChange={set("model")} data-testid="booking-model" className="h-10 text-sm" />
          <Input placeholder="Tahun" type="number" value={f.year} onChange={set("year")} data-testid="booking-year" className="h-10 text-sm" />
          <Input placeholder="Plat nomor" value={f.plate} onChange={set("plate")} data-testid="booking-plate" className="h-10 text-sm" />
        </div>
        <Input placeholder="Alamat lengkap lokasi kendaraan" value={f.address} onChange={set("address")} data-testid="booking-address" className="h-10 text-sm" />
        <Select value={f.city} onValueChange={set("city")}>
          <SelectTrigger className="h-10 text-sm" data-testid="booking-city"><SelectValue /></SelectTrigger>
          <SelectContent>{[...new Set([...(insp.service_areas || []), insp.city, ...(meta?.cities || [])])].filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" min={minDate} value={f.scheduled_date} onChange={set("scheduled_date")} data-testid="booking-date" className="h-10 text-sm" />
          <Input type="time" value={f.scheduled_time} onChange={set("scheduled_time")} data-testid="booking-time" className="h-10 text-sm" />
        </div>
        <Textarea placeholder="Catatan untuk inspector (opsional)" value={f.notes} onChange={set("notes")} rows={2} data-testid="booking-notes" className="text-sm" />
        <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
          <span className="text-xs text-slate-500">Total biaya</span>
          <span className="text-xl font-extrabold text-blue-600" data-testid="booking-price">{rupiah(price)}</span>
        </div>
        <Button onClick={submit} disabled={busy || !price} data-testid="booking-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-11 shadow-lg shadow-blue-600/25">
          {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Pesan & Lanjut Bayar
        </Button>
        <p className="text-[11px] text-slate-400 flex items-start gap-1.5"><ShieldCheck size={13} className="text-blue-600 shrink-0 mt-0.5" /> Pembayaran ditahan (escrow) hingga laporan Anda terima.</p>
      </div>
    </Card>
  );
}

export default function InspectorDetailPage() {
  const { inspectorId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: insp, isLoading } = useQuery({ queryKey: ["inspector", inspectorId], queryFn: () => api.get(`/inspectors/${inspectorId}`).then((r) => r.data) });
  if (isLoading) return <Spinner />;
  if (!insp) return <EmptyState title="Inspector tidak ditemukan" />;

  const toggleFav = async () => {
    try {
      if (insp.is_favorite) await api.delete(`/users/me/favorites/${insp.user_id}`); else await api.post(`/users/me/favorites/${insp.user_id}`);
      qc.invalidateQueries({ queryKey: ["inspector", inspectorId] }); qc.invalidateQueries({ queryKey: ["favorites"] }); qc.invalidateQueries({ queryKey: ["inspectors"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="inspector-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={16} /> Kembali</button>
      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <Card>
            <div className="flex flex-col sm:flex-row gap-5">
              <img src={insp.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(insp.name)}&background=2563eb&color=fff`} alt={insp.name} className="w-28 h-28 rounded-2xl object-cover" />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2" data-testid="inspector-name">{insp.name} <ShieldCheck size={20} className="text-blue-600" fill="#dbeafe" /></h1>
                    <p className="text-sm text-amber-500 font-bold flex items-center gap-1 mt-1"><Star size={14} fill="#f59e0b" /> {insp.rating_avg || "Baru"} <span className="text-slate-400 font-medium">({insp.rating_count} ulasan)</span> <span className={`ml-2 text-[10px] font-bold rounded-full px-2 py-0.5 ${insp.is_online ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{insp.is_online ? "Online" : "Offline"}</span></p>
                  </div>
                  <Button variant="outline" onClick={toggleFav} data-testid="detail-fav-btn" className="rounded-xl h-10">
                    <Heart size={16} className={insp.is_favorite ? "text-red-500" : "text-slate-400"} fill={insp.is_favorite ? "#ef4444" : "none"} /><span className="ml-1.5 text-xs font-bold hidden sm:inline">{insp.is_favorite ? "Favorit" : "Simpan"}</span>
                  </Button>
                </div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{insp.bio}</p>
                <div className="flex flex-wrap gap-4 mt-4 text-xs font-semibold text-slate-500">
                  <span className="flex items-center gap-1.5"><MapPin size={14} className="text-blue-600" /> {insp.city}</span>
                  <span className="flex items-center gap-1.5"><Briefcase size={14} className="text-blue-600" /> {insp.experience_years} tahun pengalaman</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-blue-600" /> {insp.completed_count} inspeksi selesai</span>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card title="Layanan & Harga">
              <div className="space-y-2">
                {insp.vehicle_types.map((v) => (
                  <div key={v} className="flex items-center justify-between text-sm border border-slate-100 rounded-xl px-4 py-3">
                    <span className="font-semibold text-slate-700">{VEHICLE_LABELS[v]}</span><span className="font-extrabold text-blue-600">{rupiah(insp.prices[v])}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Area Layanan & Sertifikasi">
              <div className="flex flex-wrap gap-1.5">{insp.service_areas.map((a) => <span key={a} className="text-xs font-semibold bg-slate-100 text-slate-600 rounded-full px-3 py-1">{a}</span>)}</div>
              <div className="mt-4 space-y-2">{insp.certifications.map((c) => <p key={c} className="text-xs text-slate-600 flex items-center gap-2"><Award size={14} className="text-amber-500" /> {c}</p>)}</div>
            </Card>
          </div>

          <Card title={`Ulasan (${insp.reviews.length})`}>
            {!insp.reviews.length ? <p className="text-sm text-slate-400">Belum ada ulasan.</p> : (
              <div className="divide-y divide-slate-50">
                {insp.reviews.slice(0, 8).map((r) => (
                  <div key={r.review_id} className="py-3.5 flex gap-3">
                    <img src={r.user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(r.user?.name || "U")}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                    <div>
                      <div className="flex items-center gap-2"><p className="text-[13px] font-bold text-slate-900">{r.user?.name}</p><span className="text-[11px] text-slate-400">{fmtDate(r.created_at)}</span></div>
                      <p className="text-amber-400 text-xs">{"★".repeat(r.rating)}<span className="text-slate-200">{"★".repeat(5 - r.rating)}</span></p>
                      <p className="text-sm text-slate-600 mt-1">{r.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
        <div><BookingForm insp={insp} initialType={sp.get("jenis")} /></div>
      </div>
    </div>
  );
}
